import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import ProjectsPage from "./pages/ProjectsPage1";
import Darklogo from "./components/Darklogo";
import { useState, useEffect } from "react";

import ScrollToTop from "./components/ScrollToTop";

import { AnimatePresence } from "framer-motion";

import ContectPage from "./pages/ContectPage";

import LoadingPage from "./pages/LoadingPage";

const SECTIONS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "projects", label: "Projects" },
  { id: "contact", label: "Contact" },
  { id: "articles", label: "Articles" },
  { id: "footer", label: "Footer" },
];

const App = () => {
  const location = useLocation();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("home");
  const [showSliderLabel, setShowSliderLabel] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.classList.contains("dark");
  });
  const [fadeClass, setFadeClass] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pageAnimated, setPageAnimated] = useState(true);
  const [showLogoAnim, setShowLogoAnim] = useState(false);

  // 切换模式
  const toggleDarkMode = () => {
    setIsTransitioning(true);
    setPageAnimated(false);
    setTimeout(() => {
      setIsDarkMode((prev) => {
        const newMode = !prev;
        document.documentElement.classList.toggle("dark", newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
        return newMode;
      });
    }, 1000);
    setTimeout(() => {
      setIsTransitioning(false);
      setShowLogoAnim(false);
    }, 1000);
  };

  // 进度 & section 滚动监听只在首页生效
  useEffect(() => {
    if (location.pathname !== "/") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-50% 0px -40% 0px", threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
    return () => {
      SECTIONS.forEach(({ id }) => {
        const section = document.getElementById(id);
        if (section) observer.unobserve(section);
      });
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!activeSection || location.pathname !== "/") return;
    setShowSliderLabel(true);
    const timeout = setTimeout(() => {
      setShowSliderLabel(false);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [activeSection, location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  const [loading, setLoading] = useState(true);

  // 页面跳转后重置 activeSection
  useEffect(() => {
    if (location.pathname !== "/" || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-50% 0px -40% 0px", threshold: 0 }
    );

    // 观察所有section
    SECTIONS.forEach(({ id }) => {
      const section = document.getElementById(id);
      if (section) {
        observer.observe(section);
      }
    });

    // 清理函数
    return () => {
      SECTIONS.forEach(({ id }) => {
        const section = document.getElementById(id);
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, [location.pathname, loading, activeSection]); // 添加 activeSection 作为依赖

  useEffect(() => {
    let animationFrameId;
    let lastUpdate = Date.now();
    const updateScrollProgress = () => {
      const now = Date.now();
      if (now - lastUpdate > 50) {
        const scrollTop = window.scrollY;
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrolled = (scrollTop / docHeight) * 100;
        setScrollProgress(scrolled);
        lastUpdate = now;
      }
      animationFrameId = requestAnimationFrame(updateScrollProgress);
    };
    animationFrameId = requestAnimationFrame(updateScrollProgress);
    return () => cancelAnimationFrame(animationFrameId);
  }, [location.pathname]);

  return (
    <div className="relative">
      {/* 背景层 */}
      <div className="fixed top-0 left-0 w-full h-full bg-orange-100 dark:bg-black bg-no-repeat bg-cover overflow-hidden" />
      {isTransitioning && (
        <div className="fixed top-0 left-0 w-full h-full z-50 pointer-events-none">
          <div className="w-full h-full bg-black dark:bg-orange-100 mask-overlay flex items-center justify-center">
            <Darklogo
              className="w-[300px] h-[300px]"
              isDark={!isDarkMode}
              show={showLogoAnim}
            />
          </div>
        </div>
      )}

      {/* 左侧进度条和标签栏只在首页显示 */}

      <div className="fixed top-0 left-0 h-full z-50 flex items-center">
        <div className="w-[4px] relative h-full">
          <div
            className="absolute top-0 left-0 w-full bg-pink-500 dark:bg-green-500 origin-top transition-all"
            style={{ height: `${scrollProgress}%` }}
          />
        </div>
        <div
          className={`absolute left-full ml-2 text-orange-600 dark:text-green-300 transition-opacity ${
            showSliderLabel ? "opacity-100" : "opacity-0"
          }`}
          style={{ top: `calc(${scrollProgress}%` }}
        >
          {SECTIONS.find((s) => s.id === activeSection)?.label}
        </div>
      </div>

      {/* 页面结构 */}
      <div className="relative z-40 bg-orange-100 dark:bg-black dark:text-white text-black">
        <ScrollToTop />

        <LoadingPage isDarkMode={isDarkMode} />
        <AnimatePresence mode="wait">
          <Routes location={location}>
            <Route
              path="/"
              element={
                <HomePage
                  //key={location.pathname} // ✅ 强制刷新组件
                  toggleDarkMode={toggleDarkMode}
                  isDarkMode={isDarkMode}
                  pageAnimated={pageAnimated}
                  loading={loading} // ✅ 传入 loading
                  setLoading={setLoading} // ✅ 传入 setLoading
                />
              }
            />
            <Route
              path="/projects"
              element={
                <ProjectsPage
                  toggleDarkMode={toggleDarkMode}
                  isDarkMode={isDarkMode}
                />
              }
            />
            <Route
              path="/Contact"
              element={
                <ContectPage
                  toggleDarkMode={toggleDarkMode}
                  isDarkMode={isDarkMode}
                />
              }
            />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default App;
